import React, { createContext, useContext, useEffect, useState } from 'react';

// Add new strings here. Always provide both en and es. Use {placeholder}
// tokens for variables — see t('app.trial_days_left_many', { n: 5 }).
const translations = {
  en: {
    // Login
    'login.headline': 'Every crew, every yard, on the fastest route of the day.',
    'login.footer': '© 2026 MowStop · Route smarter',
    'login.welcome_back': 'Welcome back',
    'login.create_account_title': 'Create your account',
    'login.sub_login': "Sign in to plan today's routes.",
    'login.sub_register': 'Start your 14-day free trial — no credit card required for the first step.',
    'login.business_name': 'Business name',
    'login.business_name_placeholder': 'Your Landscaping Co.',
    'login.work_email': 'Work email',
    'login.work_email_placeholder': 'you@greencrew.com',
    'login.password': 'Password',
    'login.password_placeholder': '••••••••••',
    'login.sign_in': 'Sign in',
    'login.create_account': 'Create account',
    'login.please_wait': 'Please wait…',
    'login.new_crew_prefix': 'New crew?',
    'login.start_free_trial_link': 'Start a free trial →',
    'login.already_have_account': 'Already have an account?',
    'login.sign_in_link': 'Sign in →',
    'login.error_generic': 'Something went wrong',
    'login.error_connect': 'Could not connect to server. Please try again.',
    'login.account_created_signin': 'Account created — please sign in',
    'login.referred_by': 'Referred by',
    'login.check_email_title': 'Check your email',
    'login.check_email_body': "We sent a confirmation link to {email}. Click it to activate your account, then sign in.",
    'login.check_email_hint': "Don't see it? Check your spam folder — it can take a minute to arrive.",
    'login.back_to_signin': 'Back to sign in',
    'login.confirmed_banner': 'Email confirmed — please sign in.',
    'login.forgot_link': 'Forgot password?',
    'login.forgot_title': 'Reset your password',
    'login.forgot_sub': "Enter your email and we'll send you a reset link.",
    'login.send_reset_link': 'Send reset link',
    'login.forgot_sent_body': "If an account exists for {email}, we've sent a password reset link. It expires in 1 hour.",
    'reset.title': 'Choose a new password',
    'reset.sub': 'Enter a new password for your account.',
    'reset.new_password': 'New password',
    'reset.confirm_password': 'Confirm password',
    'reset.submit': 'Update password',
    'reset.saving': 'Updating…',
    'reset.success_title': 'Password updated',
    'reset.success_body': 'You can now sign in with your new password.',
    'reset.go_to_signin': 'Go to sign in',
    'reset.mismatch': "Passwords don't match",
    'reset.too_short': 'Password must be at least 8 characters',
    'reset.invalid_title': 'Link not valid',
    'reset.invalid_link': 'This reset link is invalid or has expired. Request a new one from the sign-in page.',
    'reset.error': 'Could not reset your password. Please try again.',
    // App shell
    'app.clients': 'Clients',
    'app.route': 'Route',
    'app.calendar': 'Calendar',
    'app.reports': 'Reports',
    'app.team': 'Team',
    'app.partners': 'Partners',
    'app.admin': 'Admin',
    'app.billing': 'Billing',
    'app.sign_out': 'Sign out',
    'app.loading_dots': 'Loading…',
    'app.all_clients': 'All clients',
    'app.add_client': '+ Add client',
    'app.loading_clients': 'Loading clients…',
    'app.plan_limit_warning': '⚠ You have {n} clients. The Solo plan covers up to 150 — consider a Team plan as you grow.',
    'app.trial_days_left_one': '⏰ {n} day left in your free trial',
    'app.trial_days_left_many': '⏰ {n} days left in your free trial',
    'app.past_due_prefix': '⚠ Payment failed —',
    'app.update_your_card': 'update your card',
    'app.open_menu': 'Open menu',
    'app.close_menu': 'Close menu',
    'app.expand_sidebar': 'Expand sidebar',
    'app.collapse_sidebar': 'Collapse sidebar',
    'app.menu_navigate': 'Navigate',
    'app.menu_account': 'Account',
    'app.menu_language': 'Language',
    'app.skip_to_main': 'Skip to main content',
    'app.close': 'Close',
    'app.opening_dots': 'Opening…',
    'app.manage_billing_title': 'Manage billing',
    'app.portal_error': 'Could not open billing portal',
    'app.account': 'Account',
    // Profile / account screen
    'profile.account': 'Account',
    'profile.display_name': 'Display name',
    'profile.display_name_hint': 'This is how your name shows up in MowStop. Use a nickname if you like.',
    'profile.display_name_placeholder': 'e.g. Carlos',
    'profile.save_name': 'Save name',
    'profile.name_saved': 'Display name saved.',
    'profile.name_error': 'Could not save display name',
    'profile.role': 'Role',
    'profile.change_email': 'Change email',
    'profile.current_password': 'Current password',
    'profile.new_email': 'New email',
    'profile.update_email': 'Update email',
    'profile.email_saved': 'Email changed to {email}. Use it next time you sign in.',
    'profile.email_error': 'Could not change email',
    'profile.change_password': 'Change password',
    'profile.new_password': 'New password',
    'profile.confirm_password': 'Confirm new password',
    'profile.update_password': 'Update password',
    'profile.pw_saved': 'Password updated.',
    'profile.pw_error': 'Could not change password',
    'profile.pw_mismatch': 'New passwords do not match',
    // Team management
    'team.title': 'Team',
    'team.loading': 'Loading team…',
    'team.err_load': 'Could not load team',
    'team.err_add_member': 'Could not add member',
    'team.err_add_location': 'Could not add location',
    'team.err_update_member': 'Could not update member',
    'team.err_remove_member': 'Could not remove member',
    'team.confirm_remove': 'Remove {who} from the team?',
    'team.confirm_remove_default': 'this member',
    'team.member_added_title': '✓ Member added — share these login details',
    'team.login_label': 'Login:',
    'team.email_label': 'Email:',
    'team.temp_password_label': 'Temp password:',
    'team.copy': 'Copy',
    'team.copy_password_aria': 'Copy temporary password',
    'team.existing_account_note': 'This email already had a MowStop account — they sign in with their existing password.',
    'team.done': 'Done',
    'team.locations': 'Locations',
    'team.add_location': '+ Add location',
    'team.cancel': 'Cancel',
    'team.location_name': 'Location name',
    'team.city': 'City',
    'team.location_name_placeholder': 'North Houston',
    'team.city_placeholder': 'Houston',
    'team.saving': 'Saving…',
    'team.create_location': 'Create location',
    'team.no_locations': 'No locations yet.',
    'team.members': 'Team members',
    'team.add_member': '+ Add member',
    'team.name': 'Name',
    'team.email': 'Email',
    'team.name_placeholder': 'Carlos R.',
    'team.email_placeholder': 'carlos@email.com',
    'team.role': 'Role',
    'team.location': 'Location',
    'team.choose_location': 'Choose location…',
    'team.no_location': 'No location',
    'team.role_crew_manager': 'Crew Manager',
    'team.role_manager': 'Manager',
    'team.role_location_manager': 'Location Manager',
    'team.role_owner': 'Owner',
    'team.all_locations': 'all locations',
    'team.adding': 'Adding…',
    'team.add_member_btn': 'Add member',
    'team.remove': 'Remove',
    'team.change_role': 'Change role',
    'team.change_location': 'Change location',
    'team.location_pin_aria': 'Location',
    // Accessibility helpers
    'a11y.required': 'required',
    'a11y.close_dialog': 'Close dialog',
    'a11y.drag_to_reorder': 'Drag to reorder',
    'a11y.move_up': 'Move up',
    'a11y.move_down': 'Move down',
    'a11y.previous_month': 'Previous month',
    'a11y.next_month': 'Next month',
    'a11y.previous_week': 'Previous week',
    'a11y.next_week': 'Next week',
    'a11y.day_of_week': 'Day {date}',
    'a11y.remove_stop': 'Remove stop',
    'a11y.move_stop': 'Move stop',
    'a11y.toggle_versions': 'Show saved routes',
    'a11y.drop_zone_shift': 'Drop zone for {shift} shift',
    'a11y.drop_zone_day': 'Drop zone for {day}',
    'a11y.select_date': 'Select date',
    'a11y.select_crew': 'Assign crew member',
    'a11y.search_clients': 'Search clients',
    'a11y.add_to_shift': 'Add to {shift}',
    'a11y.assign_to_shift': 'Assign to {shift}',
    'a11y.unassign': 'Unassign stop',
    'a11y.mark_done': 'Mark stop as done',
    'a11y.open_payment': 'Open payment for stop',
    'a11y.client_actions': 'Client actions',
    'a11y.client_actions_for': 'Actions for {name}',
    'a11y.clear_payment': 'Clear payment',
    'a11y.delete_visit': 'Delete visit',
    'a11y.select_plan': 'Select plan',
    'a11y.address_suggestions': 'Address suggestions',
    'a11y.street_address': 'Street address',
    'a11y.search_tenants': 'Search companies or email',
    'a11y.language': 'Language',
    // Reports
    'reports.from': 'From',
    'reports.to': 'To',
    'reports.location': 'Location',
    'reports.all_locations': 'All locations',
    'reports.search': 'Search',
    'reports.routes': 'Routes',
    'reports.charged': 'Charged',
    'reports.collected': 'Collected',
    'reports.outstanding': 'Outstanding',
    'reports.loading': 'Loading sales summary…',
    'reports.empty': 'No completed routes in this date range.',
    'reports.download_csv': 'Download CSV / Excel',
    'reports.stops_count_one': '{n} stop · {done} done',
    'reports.stops_count_many': '{n} stops · {done} done',
    'reports.due': 'due',
    'reports.paid': 'paid',
    'reports.balance_short': 'bal',
    'reports.out_short': 'out',
    'reports.collected_short': 'collected',
    'reports.morning': 'Morning',
    'reports.afternoon': 'Afternoon',
    'reports.evening': 'Evening',
    'reports.manual': 'Manual',
    'reports.expand_route': 'Expand route details',
    'reports.collapse_route': 'Collapse route details',
    // Payment modal
    'pay.title': 'Payment — {who}',
    'pay.amount_due': 'Amount due ($)',
    'pay.amount_paid_today': 'Amount paid today ($)',
    'pay.tip': 'Tip ($)',
    'pay.notes_optional': 'Notes (optional)',
    'pay.notes_placeholder': 'e.g. will pay rest next week',
    'pay.amount_due_summary': 'Amount due',
    'pay.paid_summary': 'Paid',
    'pay.tip_summary': 'Tip',
    'pay.balance_due': 'Balance due',
    'pay.cancel': 'Cancel',
    'pay.save': 'Save payment',
    'pay.saving': 'Saving…',
    'pay.error_generic': 'Failed to save payment.',
    // Recurring modal
    'recur.title': 'Visit schedule',
    'recur.visit_type': 'Visit type',
    'recur.type_recurring': 'Recurring',
    'recur.type_one_time': 'One-time',
    'recur.type_none': 'None',
    'recur.visit_date': 'Visit date',
    'recur.frequency': 'Frequency',
    'recur.freq_weekly': 'Every week',
    'recur.freq_biweekly': 'Every 2 weeks',
    'recur.freq_custom': 'Every X weeks',
    'recur.freq_monthly': 'Every month',
    'recur.every_how_many_weeks': 'Every how many weeks?',
    'recur.preferred_day_optional': 'Preferred day (optional)',
    'recur.any_day': 'Any day',
    'recur.day_of_month': 'Day of month',
    'recur.day_of_month_placeholder': 'e.g. 15',
    'recur.next_visit_date': 'Next visit date',
    'recur.next_visit_hint': 'Leave blank to auto-calculate',
    'recur.amount_each_visit': 'Amount due each visit ($)',
    'recur.schedule_notes': 'Schedule notes (optional)',
    'recur.schedule_notes_placeholder': 'e.g. call 30 min ahead',
    'recur.cancel': 'Cancel',
    'recur.save': 'Save schedule',
    'recur.saving': 'Saving…',
    'recur.error_generic': 'Failed to save schedule.',
    'recur.day_sunday': 'Sunday',
    'recur.day_monday': 'Monday',
    'recur.day_tuesday': 'Tuesday',
    'recur.day_wednesday': 'Wednesday',
    'recur.day_thursday': 'Thursday',
    'recur.day_friday': 'Friday',
    'recur.day_saturday': 'Saturday',
    // AddClient
    'add_client.title': 'Add new client',
    'add_client.section_client_info': 'Client info',
    'add_client.section_visit_schedule': 'Visit schedule',
    'add_client.full_name': 'Full name',
    'add_client.full_name_placeholder': 'Maria Gonzalez',
    'add_client.phone': 'Phone number',
    'add_client.phone_placeholder': '(713) 555-0100',
    'add_client.street': 'Street address',
    'add_client.street_placeholder': 'Start typing an address…',
    'add_client.street_hint': 'Pick a suggestion, or type it in manually.',
    'add_client.city': 'City, state, ZIP',
    'add_client.city_placeholder': 'Houston, TX 77001',
    'add_client.notes': 'Notes',
    'add_client.notes_placeholder': 'Gate code, special instructions...',
    'add_client.visit_type': 'Visit type',
    'add_client.type_recurring': 'Recurring',
    'add_client.type_one_time': 'One-time',
    'add_client.type_none': 'None',
    'add_client.preferred_shift': 'Preferred shift (optional)',
    'add_client.preferred_shift_hint': "Used to auto-place this client when building a day's route.",
    'add_client.no_preference': 'No preference (Morning)',
    'add_client.shift_morning': 'Morning',
    'add_client.shift_afternoon': 'Afternoon',
    'add_client.shift_evening': 'Evening',
    'add_client.visit_date': 'Visit date',
    'add_client.frequency': 'Frequency',
    'add_client.freq_weekly': 'Every week',
    'add_client.freq_biweekly': 'Every 2 weeks',
    'add_client.freq_custom_weeks': 'Every X weeks',
    'add_client.freq_monthly': 'Every month',
    'add_client.every_how_many_weeks': 'Every how many weeks?',
    'add_client.preferred_day': 'Preferred day (optional)',
    'add_client.any_day': 'Any day',
    'add_client.day_of_month': 'Day of month',
    'add_client.day_of_month_placeholder': 'e.g. 15',
    'add_client.next_visit_date_optional': 'Next visit date (optional)',
    'add_client.amount_per_visit': 'Amount due per visit ($)',
    'add_client.schedule_notes_optional': 'Schedule notes (optional)',
    'add_client.schedule_notes_placeholder': 'e.g. call 30 min ahead',
    'add_client.cancel': 'Cancel',
    'add_client.save': 'Save client',
    'add_client.saving': 'Saving...',
    'add_client.err_required_fields': 'Name, street and city are required.',
    'add_client.err_save': 'Failed to add client. Please try again.',
    'add_client.day_sunday': 'Sunday',
    'add_client.day_monday': 'Monday',
    'add_client.day_tuesday': 'Tuesday',
    'add_client.day_wednesday': 'Wednesday',
    'add_client.day_thursday': 'Thursday',
    'add_client.day_friday': 'Friday',
    'add_client.day_saturday': 'Saturday',
    // ClientList
    'client_list.search_placeholder': 'Search clients...',
    'client_list.sort_by': 'Sort clients',
    'client_list.sort_az': 'A — Z',
    'client_list.sort_date': 'By schedule date',
    'client_list.count_one': '{n} client',
    'client_list.count_many': '{n} clients',
    'client_list.view_archived': 'View archived',
    'client_list.hide_archived': 'Hide archived',
    'client_list.loading': 'Loading...',
    'client_list.none_found': 'No clients found',
    'client_list.archived_header': 'Archived clients ({n})',
    'client_list.none_archived': 'No archived clients',
    'client_list.due': 'Due',
    'client_list.edit_client': 'Edit client',
    'client_list.visit_history': 'Visit history',
    'client_list.schedule': 'Schedule',
    'client_list.archive_client': 'Archive client',
    'client_list.restore_client': 'Restore client',
    'client_list.delete_client': 'Delete client',
    'client_list.confirm_delete': 'Delete {name} permanently?',
    'client_list.err_delete': 'Failed to delete client.',
    'client_list.err_archive': 'Failed to archive client.',
    'client_list.err_restore': 'Failed to restore client.',
    'client_list.err_load_archived': 'Failed to load archived clients.',
    'client_list.err_update': 'Failed to update client.',
    'client_list.edit_title': 'Edit — {name}',
    'client_list.field_name': 'Name',
    'client_list.field_phone': 'Phone',
    'client_list.field_street': 'Street',
    'client_list.field_city': 'City',
    'client_list.field_notes': 'Notes',
    'client_list.field_preferred_shift': 'Preferred shift',
    'client_list.cancel': 'Cancel',
    'client_list.save_changes': 'Save changes',
    // ClientHistory
    'history.total_cuts': 'Total cuts',
    'history.collected': 'Collected',
    'history.outstanding': 'Outstanding',
    'history.unpaid_one': '{n} unpaid cut — {amount} outstanding',
    'history.unpaid_many': '{n} unpaid cuts — {amount} outstanding',
    'history.filter_all': 'All',
    'history.filter_paid': 'Paid',
    'history.filter_unpaid': 'Unpaid',
    'history.add_visit': '+ Add visit',
    'history.add_visit_title': 'Add manual visit',
    'history.visit_date': 'Visit date',
    'history.amount_charged': 'Amount charged',
    'history.amount_paid': 'Amount paid',
    'history.tip': 'Tip',
    'history.notes': 'Notes',
    'history.notes_placeholder': 'Optional notes...',
    'history.completed': 'Completed',
    'history.paid': 'Paid',
    'history.cancel': 'Cancel',
    'history.save_visit': 'Save visit',
    'history.saving': 'Saving...',
    'history.loading': 'Loading history...',
    'history.empty': 'No visits found.',
    'history.status_completed': '✓ Completed',
    'history.status_pending': '○ Pending',
    'history.status_paid': '$ Paid',
    'history.status_unpaid': '$ Unpaid',
    'history.mark_complete': '✓ Mark complete',
    'history.mark_incomplete': '✕ Mark incomplete',
    'history.mark_paid': '$ Mark paid',
    'history.charged': 'Charged',
    'history.paid_label': 'Paid',
    'history.tip_label': 'Tip',
    'history.balance_due': 'Balance due',
    'history.slot_manual': '📝 Manual entry',
    'history.slot_morning': '☀️ Morning',
    'history.slot_afternoon': '🌤️ Afternoon',
    'history.slot_evening': '🌙 Evening',
    'history.confirm_clear_payment': 'Clear payment for this visit?',
    'history.confirm_delete_visit': 'Delete this visit permanently?',
    'history.err_mark_paid': 'Failed to mark as paid.',
    'history.err_update_visit': 'Failed to update visit.',
    'history.err_clear_payment': 'Failed to clear payment.',
    'history.err_delete_visit': 'Failed to delete visit.',
    'history.err_add_visit': 'Failed to add visit.',
    'history.dialog_title': '{name} — visit history',
    // Billing screen
    'billing.title_default': 'Choose your plan',
    'billing.subtitle_default': 'Start your 14-day free trial. Cancel anytime from your account.',
    'billing.title_past_due': 'Update your payment method',
    'billing.subtitle_past_due': "Your last payment didn't go through. Open the billing portal to fix it.",
    'billing.title_canceled': 'Welcome back',
    'billing.subtitle_canceled': 'Pick a plan to reactivate your MowStop account.',
    'billing.title_expired': 'Your trial has ended',
    'billing.subtitle_expired': 'Choose a plan to keep using MowStop.',
    'billing.monthly': 'Monthly',
    'billing.yearly': 'Yearly',
    'billing.per_mo': '/mo',
    'billing.per_yr': '/yr',
    'billing.billed_monthly': 'Billed monthly · Cancel anytime',
    'billing.yearly_note': '~$29.92/mo · 2 months free',
    'billing.start_free_trial': 'Start free trial',
    'billing.save_109': 'Save $109',
    'billing.trial_perks': '✓ 14-day free trial · ✓ No setup fees · ✓ Cancel anytime',
    'billing.already_customer_manage': 'Already a customer? Manage billing',
    'billing.checkout_error': 'Could not start checkout',
    'billing.no_checkout_url': 'No checkout URL returned',
    'billing.no_portal_url': 'No portal URL returned',
    'billing.portal_error': 'Could not open billing portal',
    // Calendar route planner
    'cal.morning': 'Morning',
    'cal.afternoon': 'Afternoon',
    'cal.evening': 'Evening',
    'cal.today': 'Today',
    'cal.note': '📅 Plan up to 6 months out. Approve a day and it auto-populates the Route tab on that date — or open it early with “Open in Route tab.”',
    'cal.approve_day': '✓ Approve day',
    'cal.loading': 'Loading…',
    'cal.no_route_title': 'No route yet for this day',
    'cal.no_route_sub': 'Auto-build from clients due this date, or open the Route tab to build it by hand.',
    'cal.autobuild': '⚡ Auto-build from due clients',
    'cal.open_route': 'Open in Route tab →',
    'cal.open_route_edit': 'Open in Route tab to edit →',
    'cal.approved': 'Approved',
    'cal.draft': 'Draft',
    'cal.approve': 'Approve',
    'cal.unapprove': 'Unapprove',
    'cal.no_stops': 'No stops.',
    'cal.stops_one': '{n} stop',
    'cal.stops_many': '{n} stops',
    'cal.expected': '{amount} expected',
    'cal.move_up': 'Move up',
    'cal.move_down': 'Move down',
    'cal.err_load': 'Could not load the calendar.',
    'cal.err_day': 'Could not load routes for this day.',
    'cal.err_nothing_approve': 'Nothing to approve — add stops first.',
    'cal.err_approve_day': 'Could not approve the day.',
    'cal.err_add_stops': 'Add stops before approving.',
    'cal.err_update': 'Could not update the route.',
    'cal.err_none_due': 'No clients are due on this date.',
    'cal.err_autobuild': 'Could not auto-build the route.',
    'cal.err_reorder': 'Could not save the new order.',
    'cal.err_add': 'Could not add the client.',
    'cal.err_move': 'Could not move the stop.',
    'cal.err_remove': 'Could not remove the stop.',
    'cal.add_clients': '+ Add clients',
    'cal.done_adding': 'Done',
    'cal.search_clients': 'Search clients to add…',
    'cal.no_available': 'No more clients to add for this day.',
    'cal.due': 'Due',
    'cal.saving': 'Saving…',
    'cal.empty_shift': 'No clients yet — auto-build or add them above.',
    'cal.move_to': 'Move to',
    'cal.move': 'Move',
    'cal.remove': 'Remove',
    'cal.autosaved': 'All changes saved automatically',
    'cal.view_month': 'Month',
    'cal.view_week': 'Week',
    'cal.err_week': 'Could not load the week.',
    'cal.week_hint': 'Drag a client to any day or shift.',
    'cal.week_card_hint': 'Drag to another day or shift',
    // Route planner
    'route.map_aria': 'Route map',
    'route.err_map': 'Could not load map.',
    'route.err_add_stop_save': 'Add at least one stop before saving.',
    'route.err_none_due_route': 'No clients due for this date that aren’t already on the route.',
    'route.err_load_due': 'Could not load due clients.',
    'route.err_assign_all': 'Assign every client to a Morning, Afternoon, or Evening shift before approving.',
    'route.err_add_stop_approve': 'Add at least one stop before approving.',
    'route.err_approve': 'Could not approve the route. Please try again.',
    'route.err_save_first_end': 'Save the route first before ending it.',
    'route.confirm_end_shift': 'End the {shift} route? It will appear in your reports.',
    'route.err_end': 'Failed to end route.',
    'route.err_save_first_complete': 'Save the route first before marking stops complete.',
    'route.err_update_stop': 'Failed to update stop.',
    'route.err_save_first': 'Save the route first.',
    'route.confirm_clear_all': 'Remove all stops from all routes today?',
    'route.err_slot_full': '{shift} route is full (max {max} stops).',
    'route.err_save_route': 'Failed to save route.',
    'route.err_approve_first_nav': 'Approve the route first to start navigation.',
    'route.navigate': 'Navigate',
    'route.save': 'Save',
    'route.done': 'Done',
    'route.pay': 'Pay',
    'route.unassign': 'Unassign',
    'route.load_due': 'Load clients due',
    'route.loading': 'Loading…',
    'route.approve_route': 'Approve route',
    'route.approving': 'Approving…',
    'route.assign_n_to_shift': 'Assign {n} to a shift',
    'route.clear_all': 'Clear all stops',
    'route.end_shift': 'End {shift} route',
    'route.ending': 'Ending...',
    'route.saved_menu': 'Saved',
    'route.no_versions': 'No saved routes yet',
    'route.unassigned_crew': 'Unassigned crew',
    'route.crew': 'Crew',
    'route.approved_ready': 'Approved · ready to navigate',
    'route.waiting_approval': 'Waiting for approval',
    'route.total_due': 'Total due',
    'route.collected': 'Collected',
    'route.outstanding': 'Outstanding',
    'route.amt_due': '{amt} due',
    'route.amt_collected': '{amt} collected',
    'route.amt_balance': '{amt} balance',
    'route.amt_outstanding': '{amt} outstanding',
    'route.n_done': '{n} done',
    'route.unassigned': 'Unassigned',
    'route.unassigned_title': 'Unassigned ({n})',
    'route.unassigned_hint': 'Drag to a route or tap slot buttons',
    'route.drop_here_shift': 'Drop here to add to {shift}',
    'route.empty_shift_hint': 'Drag clients here or tap slot button on each card',
    'route.marker_stop': 'Stop {letter}',
    'route.nav_shift_aria': 'Navigate {shift} route',
    'route.save_shift_aria': 'Save {shift} route',
    'route.nav_title_enabled': 'Open turn-by-turn in Google Maps',
    'route.nav_title_disabled': 'Approve the route to enable navigation',
    'route.version_saved': 'Saved',
    'route.client_default': 'Client',
  },
  es: {
    // Login
    'login.headline': 'Cada equipo, cada jardín, en la ruta más rápida del día.',
    'login.footer': '© 2026 MowStop · Mejor ruta',
    'login.welcome_back': 'Bienvenido de nuevo',
    'login.create_account_title': 'Crea tu cuenta',
    'login.sub_login': 'Inicia sesión para planear las rutas de hoy.',
    'login.sub_register': 'Empieza tu prueba gratis de 14 días — sin tarjeta de crédito para el primer paso.',
    'login.business_name': 'Nombre del negocio',
    'login.business_name_placeholder': 'Tu compañía de jardinería',
    'login.work_email': 'Correo de trabajo',
    'login.work_email_placeholder': 'tu@equipoverde.com',
    'login.password': 'Contraseña',
    'login.password_placeholder': '••••••••••',
    'login.sign_in': 'Iniciar sesión',
    'login.create_account': 'Crear cuenta',
    'login.please_wait': 'Espera…',
    'login.new_crew_prefix': '¿Nuevo equipo?',
    'login.start_free_trial_link': 'Empezar prueba gratis →',
    'login.already_have_account': '¿Ya tienes cuenta?',
    'login.sign_in_link': 'Iniciar sesión →',
    'login.error_generic': 'Algo salió mal',
    'login.error_connect': 'No se pudo conectar con el servidor. Intenta de nuevo.',
    'login.account_created_signin': 'Cuenta creada — inicia sesión',
    'login.referred_by': 'Te recomendó',
    'login.check_email_title': 'Revisa tu correo',
    'login.check_email_body': 'Enviamos un enlace de confirmación a {email}. Haz clic para activar tu cuenta y luego inicia sesión.',
    'login.check_email_hint': '¿No lo ves? Revisa tu carpeta de spam — puede tardar un minuto en llegar.',
    'login.back_to_signin': 'Volver a iniciar sesión',
    'login.confirmed_banner': 'Correo confirmado — inicia sesión.',
    'login.forgot_link': '¿Olvidaste tu contraseña?',
    'login.forgot_title': 'Restablece tu contraseña',
    'login.forgot_sub': 'Ingresa tu correo y te enviaremos un enlace para restablecerla.',
    'login.send_reset_link': 'Enviar enlace',
    'login.forgot_sent_body': 'Si existe una cuenta para {email}, enviamos un enlace para restablecer tu contraseña. Caduca en 1 hora.',
    'reset.title': 'Elige una nueva contraseña',
    'reset.sub': 'Ingresa una nueva contraseña para tu cuenta.',
    'reset.new_password': 'Nueva contraseña',
    'reset.confirm_password': 'Confirmar contraseña',
    'reset.submit': 'Actualizar contraseña',
    'reset.saving': 'Actualizando…',
    'reset.success_title': 'Contraseña actualizada',
    'reset.success_body': 'Ya puedes iniciar sesión con tu nueva contraseña.',
    'reset.go_to_signin': 'Ir a iniciar sesión',
    'reset.mismatch': 'Las contraseñas no coinciden',
    'reset.too_short': 'La contraseña debe tener al menos 8 caracteres',
    'reset.invalid_title': 'Enlace no válido',
    'reset.invalid_link': 'Este enlace no es válido o ha caducado. Solicita uno nuevo desde la página de inicio de sesión.',
    'reset.error': 'No se pudo restablecer tu contraseña. Intenta de nuevo.',
    // App shell
    'app.clients': 'Clientes',
    'app.route': 'Ruta',
    'app.calendar': 'Calendario',
    'app.reports': 'Reportes',
    'app.team': 'Equipo',
    'app.partners': 'Socios',
    'app.admin': 'Admin',
    'app.billing': 'Facturación',
    'app.sign_out': 'Cerrar sesión',
    'app.loading_dots': 'Cargando…',
    'app.all_clients': 'Todos los clientes',
    'app.add_client': '+ Agregar cliente',
    'app.loading_clients': 'Cargando clientes…',
    'app.plan_limit_warning': '⚠ Tienes {n} clientes. El plan Solo cubre hasta 150 — considera un plan Team al crecer.',
    'app.trial_days_left_one': '⏰ Queda {n} día de tu prueba gratis',
    'app.trial_days_left_many': '⏰ Quedan {n} días de tu prueba gratis',
    'app.past_due_prefix': '⚠ Pago rechazado —',
    'app.update_your_card': 'actualiza tu tarjeta',
    'app.open_menu': 'Abrir menú',
    'app.close_menu': 'Cerrar menú',
    'app.expand_sidebar': 'Expandir barra lateral',
    'app.collapse_sidebar': 'Contraer barra lateral',
    'app.menu_navigate': 'Navegar',
    'app.menu_account': 'Cuenta',
    'app.menu_language': 'Idioma',
    'app.skip_to_main': 'Saltar al contenido principal',
    'app.close': 'Cerrar',
    'app.opening_dots': 'Abriendo…',
    'app.manage_billing_title': 'Gestionar facturación',
    'app.portal_error': 'No se pudo abrir el portal de facturación',
    'app.account': 'Cuenta',
    // Profile / account screen
    'profile.account': 'Cuenta',
    'profile.display_name': 'Nombre para mostrar',
    'profile.display_name_hint': 'Así aparece tu nombre en MowStop. Usa un apodo si quieres.',
    'profile.display_name_placeholder': 'ej. Carlos',
    'profile.save_name': 'Guardar nombre',
    'profile.name_saved': 'Nombre guardado.',
    'profile.name_error': 'No se pudo guardar el nombre',
    'profile.role': 'Rol',
    'profile.change_email': 'Cambiar correo',
    'profile.current_password': 'Contraseña actual',
    'profile.new_email': 'Nuevo correo',
    'profile.update_email': 'Actualizar correo',
    'profile.email_saved': 'Correo cambiado a {email}. Úsalo la próxima vez que inicies sesión.',
    'profile.email_error': 'No se pudo cambiar el correo',
    'profile.change_password': 'Cambiar contraseña',
    'profile.new_password': 'Nueva contraseña',
    'profile.confirm_password': 'Confirmar nueva contraseña',
    'profile.update_password': 'Actualizar contraseña',
    'profile.pw_saved': 'Contraseña actualizada.',
    'profile.pw_error': 'No se pudo cambiar la contraseña',
    'profile.pw_mismatch': 'Las contraseñas no coinciden',
    // Team management
    'team.title': 'Equipo',
    'team.loading': 'Cargando equipo…',
    'team.err_load': 'No se pudo cargar el equipo',
    'team.err_add_member': 'No se pudo agregar el miembro',
    'team.err_add_location': 'No se pudo agregar la ubicación',
    'team.err_update_member': 'No se pudo actualizar el miembro',
    'team.err_remove_member': 'No se pudo quitar el miembro',
    'team.confirm_remove': '¿Quitar a {who} del equipo?',
    'team.confirm_remove_default': 'este miembro',
    'team.member_added_title': '✓ Miembro agregado — comparte estos datos de acceso',
    'team.login_label': 'Acceso:',
    'team.email_label': 'Correo:',
    'team.temp_password_label': 'Contraseña temporal:',
    'team.copy': 'Copiar',
    'team.copy_password_aria': 'Copiar contraseña temporal',
    'team.existing_account_note': 'Este correo ya tenía una cuenta de MowStop — inicia sesión con su contraseña existente.',
    'team.done': 'Listo',
    'team.locations': 'Ubicaciones',
    'team.add_location': '+ Agregar ubicación',
    'team.cancel': 'Cancelar',
    'team.location_name': 'Nombre de la ubicación',
    'team.city': 'Ciudad',
    'team.location_name_placeholder': 'Norte de Houston',
    'team.city_placeholder': 'Houston',
    'team.saving': 'Guardando…',
    'team.create_location': 'Crear ubicación',
    'team.no_locations': 'Aún no hay ubicaciones.',
    'team.members': 'Miembros del equipo',
    'team.add_member': '+ Agregar miembro',
    'team.name': 'Nombre',
    'team.email': 'Correo',
    'team.name_placeholder': 'Carlos R.',
    'team.email_placeholder': 'carlos@correo.com',
    'team.role': 'Rol',
    'team.location': 'Ubicación',
    'team.choose_location': 'Elige ubicación…',
    'team.no_location': 'Sin ubicación',
    'team.role_crew_manager': 'Jefe de cuadrilla',
    'team.role_manager': 'Gerente',
    'team.role_location_manager': 'Gerente de ubicación',
    'team.role_owner': 'Dueño',
    'team.all_locations': 'todas las ubicaciones',
    'team.adding': 'Agregando…',
    'team.add_member_btn': 'Agregar miembro',
    'team.remove': 'Quitar',
    'team.change_role': 'Cambiar rol',
    'team.change_location': 'Cambiar ubicación',
    'team.location_pin_aria': 'Ubicación',
    // Accessibility helpers
    'a11y.required': 'requerido',
    'a11y.close_dialog': 'Cerrar diálogo',
    'a11y.drag_to_reorder': 'Arrastrar para reordenar',
    'a11y.move_up': 'Mover arriba',
    'a11y.move_down': 'Mover abajo',
    'a11y.previous_month': 'Mes anterior',
    'a11y.next_month': 'Mes siguiente',
    'a11y.previous_week': 'Semana anterior',
    'a11y.next_week': 'Semana siguiente',
    'a11y.day_of_week': 'Día {date}',
    'a11y.remove_stop': 'Quitar parada',
    'a11y.move_stop': 'Mover parada',
    'a11y.toggle_versions': 'Mostrar rutas guardadas',
    'a11y.drop_zone_shift': 'Zona para soltar en turno {shift}',
    'a11y.drop_zone_day': 'Zona para soltar en {day}',
    'a11y.select_date': 'Elegir fecha',
    'a11y.select_crew': 'Asignar miembro de cuadrilla',
    'a11y.search_clients': 'Buscar clientes',
    'a11y.add_to_shift': 'Agregar a {shift}',
    'a11y.assign_to_shift': 'Asignar a {shift}',
    'a11y.unassign': 'Desasignar parada',
    'a11y.mark_done': 'Marcar parada como hecha',
    'a11y.open_payment': 'Abrir pago para la parada',
    'a11y.address_suggestions': 'Sugerencias de dirección',
    'a11y.street_address': 'Dirección',
    'a11y.search_tenants': 'Buscar empresa o correo',
    'a11y.language': 'Idioma',
    'a11y.select_plan': 'Seleccionar plan',
    'a11y.client_actions': 'Acciones del cliente',
    'a11y.client_actions_for': 'Acciones para {name}',
    'a11y.clear_payment': 'Borrar pago',
    'a11y.delete_visit': 'Eliminar visita',
    // AddClient
    'add_client.title': 'Agregar nuevo cliente',
    'add_client.section_client_info': 'Información del cliente',
    'add_client.section_visit_schedule': 'Horario de visitas',
    'add_client.full_name': 'Nombre completo',
    'add_client.full_name_placeholder': 'María González',
    'add_client.phone': 'Número de teléfono',
    'add_client.phone_placeholder': '(713) 555-0100',
    'add_client.street': 'Dirección',
    'add_client.street_placeholder': 'Empieza a escribir una dirección…',
    'add_client.street_hint': 'Elige una sugerencia o escríbela manualmente.',
    'add_client.city': 'Ciudad, estado, código postal',
    'add_client.city_placeholder': 'Houston, TX 77001',
    'add_client.notes': 'Notas',
    'add_client.notes_placeholder': 'Código de portón, instrucciones especiales...',
    'add_client.visit_type': 'Tipo de visita',
    'add_client.type_recurring': 'Recurrente',
    'add_client.type_one_time': 'Una sola vez',
    'add_client.type_none': 'Ninguna',
    'add_client.preferred_shift': 'Turno preferido (opcional)',
    'add_client.preferred_shift_hint': 'Se usa para ubicar al cliente al armar la ruta del día.',
    'add_client.no_preference': 'Sin preferencia (Mañana)',
    'add_client.shift_morning': 'Mañana',
    'add_client.shift_afternoon': 'Tarde',
    'add_client.shift_evening': 'Noche',
    'add_client.visit_date': 'Fecha de visita',
    'add_client.frequency': 'Frecuencia',
    'add_client.freq_weekly': 'Cada semana',
    'add_client.freq_biweekly': 'Cada 2 semanas',
    'add_client.freq_custom_weeks': 'Cada X semanas',
    'add_client.freq_monthly': 'Cada mes',
    'add_client.every_how_many_weeks': '¿Cada cuántas semanas?',
    'add_client.preferred_day': 'Día preferido (opcional)',
    'add_client.any_day': 'Cualquier día',
    'add_client.day_of_month': 'Día del mes',
    'add_client.day_of_month_placeholder': 'ej. 15',
    'add_client.next_visit_date_optional': 'Próxima visita (opcional)',
    'add_client.amount_per_visit': 'Monto por visita ($)',
    'add_client.schedule_notes_optional': 'Notas del horario (opcional)',
    'add_client.schedule_notes_placeholder': 'ej. llamar 30 min antes',
    'add_client.cancel': 'Cancelar',
    'add_client.save': 'Guardar cliente',
    'add_client.saving': 'Guardando...',
    'add_client.err_required_fields': 'Nombre, calle y ciudad son obligatorios.',
    'add_client.err_save': 'No se pudo agregar el cliente. Intenta de nuevo.',
    'add_client.day_sunday': 'Domingo',
    'add_client.day_monday': 'Lunes',
    'add_client.day_tuesday': 'Martes',
    'add_client.day_wednesday': 'Miércoles',
    'add_client.day_thursday': 'Jueves',
    'add_client.day_friday': 'Viernes',
    'add_client.day_saturday': 'Sábado',
    // ClientList
    'client_list.search_placeholder': 'Buscar clientes...',
    'client_list.sort_by': 'Ordenar clientes',
    'client_list.sort_az': 'A — Z',
    'client_list.sort_date': 'Por fecha de visita',
    'client_list.count_one': '{n} cliente',
    'client_list.count_many': '{n} clientes',
    'client_list.view_archived': 'Ver archivados',
    'client_list.hide_archived': 'Ocultar archivados',
    'client_list.loading': 'Cargando...',
    'client_list.none_found': 'No se encontraron clientes',
    'client_list.archived_header': 'Clientes archivados ({n})',
    'client_list.none_archived': 'No hay clientes archivados',
    'client_list.due': 'Toca',
    'client_list.edit_client': 'Editar cliente',
    'client_list.visit_history': 'Historial de visitas',
    'client_list.schedule': 'Horario',
    'client_list.archive_client': 'Archivar cliente',
    'client_list.restore_client': 'Restaurar cliente',
    'client_list.delete_client': 'Eliminar cliente',
    'client_list.confirm_delete': '¿Eliminar a {name} permanentemente?',
    'client_list.err_delete': 'No se pudo eliminar el cliente.',
    'client_list.err_archive': 'No se pudo archivar el cliente.',
    'client_list.err_restore': 'No se pudo restaurar el cliente.',
    'client_list.err_load_archived': 'No se pudieron cargar los clientes archivados.',
    'client_list.err_update': 'No se pudo actualizar el cliente.',
    'client_list.edit_title': 'Editar — {name}',
    'client_list.field_name': 'Nombre',
    'client_list.field_phone': 'Teléfono',
    'client_list.field_street': 'Calle',
    'client_list.field_city': 'Ciudad',
    'client_list.field_notes': 'Notas',
    'client_list.field_preferred_shift': 'Turno preferido',
    'client_list.cancel': 'Cancelar',
    'client_list.save_changes': 'Guardar cambios',
    // ClientHistory
    'history.total_cuts': 'Cortes totales',
    'history.collected': 'Cobrado',
    'history.outstanding': 'Pendiente',
    'history.unpaid_one': '{n} corte sin pagar — {amount} pendiente',
    'history.unpaid_many': '{n} cortes sin pagar — {amount} pendiente',
    'history.filter_all': 'Todos',
    'history.filter_paid': 'Pagado',
    'history.filter_unpaid': 'Sin pagar',
    'history.add_visit': '+ Agregar visita',
    'history.add_visit_title': 'Agregar visita manual',
    'history.visit_date': 'Fecha de visita',
    'history.amount_charged': 'Monto cobrado',
    'history.amount_paid': 'Monto pagado',
    'history.tip': 'Propina',
    'history.notes': 'Notas',
    'history.notes_placeholder': 'Notas opcionales...',
    'history.completed': 'Completado',
    'history.paid': 'Pagado',
    'history.cancel': 'Cancelar',
    'history.save_visit': 'Guardar visita',
    'history.saving': 'Guardando...',
    'history.loading': 'Cargando historial...',
    'history.empty': 'No se encontraron visitas.',
    'history.status_completed': '✓ Completado',
    'history.status_pending': '○ Pendiente',
    'history.status_paid': '$ Pagado',
    'history.status_unpaid': '$ Sin pagar',
    'history.mark_complete': '✓ Marcar completo',
    'history.mark_incomplete': '✕ Marcar incompleto',
    'history.mark_paid': '$ Marcar pagado',
    'history.charged': 'Cobrado',
    'history.paid_label': 'Pagado',
    'history.tip_label': 'Propina',
    'history.balance_due': 'Saldo pendiente',
    'history.slot_manual': '📝 Entrada manual',
    'history.slot_morning': '☀️ Mañana',
    'history.slot_afternoon': '🌤️ Tarde',
    'history.slot_evening': '🌙 Noche',
    'history.confirm_clear_payment': '¿Borrar el pago de esta visita?',
    'history.confirm_delete_visit': '¿Eliminar esta visita permanentemente?',
    'history.err_mark_paid': 'No se pudo marcar como pagado.',
    'history.err_update_visit': 'No se pudo actualizar la visita.',
    'history.err_clear_payment': 'No se pudo borrar el pago.',
    'history.err_delete_visit': 'No se pudo eliminar la visita.',
    'history.err_add_visit': 'No se pudo agregar la visita.',
    'history.dialog_title': '{name} — historial de visitas',
    // Reports
    'reports.from': 'Desde',
    'reports.to': 'Hasta',
    'reports.location': 'Ubicación',
    'reports.all_locations': 'Todas las ubicaciones',
    'reports.search': 'Buscar',
    'reports.routes': 'Rutas',
    'reports.charged': 'Cobrado',
    'reports.collected': 'Recibido',
    'reports.outstanding': 'Pendiente',
    'reports.loading': 'Cargando resumen de ventas…',
    'reports.empty': 'No hay rutas completadas en este rango de fechas.',
    'reports.download_csv': 'Descargar CSV / Excel',
    'reports.stops_count_one': '{n} parada · {done} hechas',
    'reports.stops_count_many': '{n} paradas · {done} hechas',
    'reports.due': 'a deber',
    'reports.paid': 'pagado',
    'reports.balance_short': 'saldo',
    'reports.out_short': 'pend',
    'reports.collected_short': 'recibido',
    'reports.morning': 'Mañana',
    'reports.afternoon': 'Tarde',
    'reports.evening': 'Noche',
    'reports.manual': 'Manual',
    'reports.expand_route': 'Expandir detalles de la ruta',
    'reports.collapse_route': 'Contraer detalles de la ruta',
    // Payment modal
    'pay.title': 'Pago — {who}',
    'pay.amount_due': 'Cantidad a deber ($)',
    'pay.amount_paid_today': 'Cantidad pagada hoy ($)',
    'pay.tip': 'Propina ($)',
    'pay.notes_optional': 'Notas (opcional)',
    'pay.notes_placeholder': 'ej. paga el resto la próxima semana',
    'pay.amount_due_summary': 'Cantidad a deber',
    'pay.paid_summary': 'Pagado',
    'pay.tip_summary': 'Propina',
    'pay.balance_due': 'Saldo pendiente',
    'pay.cancel': 'Cancelar',
    'pay.save': 'Guardar pago',
    'pay.saving': 'Guardando…',
    'pay.error_generic': 'No se pudo guardar el pago.',
    // Recurring modal
    'recur.title': 'Horario de visitas',
    'recur.visit_type': 'Tipo de visita',
    'recur.type_recurring': 'Recurrente',
    'recur.type_one_time': 'Una vez',
    'recur.type_none': 'Ninguna',
    'recur.visit_date': 'Fecha de la visita',
    'recur.frequency': 'Frecuencia',
    'recur.freq_weekly': 'Cada semana',
    'recur.freq_biweekly': 'Cada 2 semanas',
    'recur.freq_custom': 'Cada X semanas',
    'recur.freq_monthly': 'Cada mes',
    'recur.every_how_many_weeks': '¿Cada cuántas semanas?',
    'recur.preferred_day_optional': 'Día preferido (opcional)',
    'recur.any_day': 'Cualquier día',
    'recur.day_of_month': 'Día del mes',
    'recur.day_of_month_placeholder': 'ej. 15',
    'recur.next_visit_date': 'Próxima fecha de visita',
    'recur.next_visit_hint': 'Déjalo en blanco para calcular automáticamente',
    'recur.amount_each_visit': 'Cantidad a deber por visita ($)',
    'recur.schedule_notes': 'Notas del horario (opcional)',
    'recur.schedule_notes_placeholder': 'ej. llamar 30 min antes',
    'recur.cancel': 'Cancelar',
    'recur.save': 'Guardar horario',
    'recur.saving': 'Guardando…',
    'recur.error_generic': 'No se pudo guardar el horario.',
    'recur.day_sunday': 'Domingo',
    'recur.day_monday': 'Lunes',
    'recur.day_tuesday': 'Martes',
    'recur.day_wednesday': 'Miércoles',
    'recur.day_thursday': 'Jueves',
    'recur.day_friday': 'Viernes',
    'recur.day_saturday': 'Sábado',
    // Billing screen
    'billing.title_default': 'Elige tu plan',
    'billing.subtitle_default': 'Empieza tu prueba gratis de 14 días. Cancela cuando quieras desde tu cuenta.',
    'billing.title_past_due': 'Actualiza tu método de pago',
    'billing.subtitle_past_due': 'Tu último pago no se procesó. Abre el portal de facturación para corregirlo.',
    'billing.title_canceled': 'Bienvenido de nuevo',
    'billing.subtitle_canceled': 'Elige un plan para reactivar tu cuenta de MowStop.',
    'billing.title_expired': 'Tu prueba ha terminado',
    'billing.subtitle_expired': 'Elige un plan para seguir usando MowStop.',
    'billing.monthly': 'Mensual',
    'billing.yearly': 'Anual',
    'billing.per_mo': '/mes',
    'billing.per_yr': '/año',
    'billing.billed_monthly': 'Facturación mensual · Cancela cuando quieras',
    'billing.yearly_note': '~$29.92/mes · 2 meses gratis',
    'billing.start_free_trial': 'Empezar prueba gratis',
    'billing.save_109': 'Ahorra $109',
    'billing.trial_perks': '✓ 14 días gratis · ✓ Sin costo de instalación · ✓ Cancela cuando quieras',
    'billing.already_customer_manage': '¿Ya eres cliente? Gestionar facturación',
    'billing.checkout_error': 'No se pudo iniciar la suscripción',
    'billing.no_checkout_url': 'No se recibió URL de pago',
    'billing.no_portal_url': 'No se recibió URL del portal',
    'billing.portal_error': 'No se pudo abrir el portal de facturación',
    // Calendar route planner
    'cal.morning': 'Mañana',
    'cal.afternoon': 'Tarde',
    'cal.evening': 'Noche',
    'cal.today': 'Hoy',
    'cal.note': '📅 Planea hasta 6 meses por adelantado. Aprueba un día y se cargará solo en la pestaña Ruta esa fecha — o ábrelo antes con “Abrir en la pestaña Ruta.”',
    'cal.approve_day': '✓ Aprobar día',
    'cal.loading': 'Cargando…',
    'cal.no_route_title': 'Aún no hay ruta para este día',
    'cal.no_route_sub': 'Crea la ruta con los clientes que tocan esta fecha, o ábrela en la pestaña Ruta para armarla a mano.',
    'cal.autobuild': '⚡ Crear ruta con clientes del día',
    'cal.open_route': 'Abrir en la pestaña Ruta →',
    'cal.open_route_edit': 'Abrir en la pestaña Ruta para editar →',
    'cal.approved': 'Aprobada',
    'cal.draft': 'Borrador',
    'cal.approve': 'Aprobar',
    'cal.unapprove': 'Quitar aprobación',
    'cal.no_stops': 'Sin paradas.',
    'cal.stops_one': '{n} parada',
    'cal.stops_many': '{n} paradas',
    'cal.expected': '{amount} esperado',
    'cal.move_up': 'Subir',
    'cal.move_down': 'Bajar',
    'cal.err_load': 'No se pudo cargar el calendario.',
    'cal.err_day': 'No se pudieron cargar las rutas de este día.',
    'cal.err_nothing_approve': 'Nada que aprobar — agrega paradas primero.',
    'cal.err_approve_day': 'No se pudo aprobar el día.',
    'cal.err_add_stops': 'Agrega paradas antes de aprobar.',
    'cal.err_update': 'No se pudo actualizar la ruta.',
    'cal.err_none_due': 'Ningún cliente toca en esta fecha.',
    'cal.err_autobuild': 'No se pudo crear la ruta.',
    'cal.err_reorder': 'No se pudo guardar el nuevo orden.',
    'cal.err_add': 'No se pudo agregar el cliente.',
    'cal.err_move': 'No se pudo mover la parada.',
    'cal.err_remove': 'No se pudo quitar la parada.',
    'cal.add_clients': '+ Agregar clientes',
    'cal.done_adding': 'Listo',
    'cal.search_clients': 'Busca clientes para agregar…',
    'cal.no_available': 'No hay más clientes para agregar este día.',
    'cal.due': 'Toca',
    'cal.saving': 'Guardando…',
    'cal.empty_shift': 'Sin clientes — usa crear ruta o agrégalos arriba.',
    'cal.move_to': 'Mover a',
    'cal.move': 'Mover',
    'cal.remove': 'Quitar',
    'cal.autosaved': 'Los cambios se guardan automáticamente',
    'cal.view_month': 'Mes',
    'cal.view_week': 'Semana',
    'cal.err_week': 'No se pudo cargar la semana.',
    'cal.week_hint': 'Arrastra un cliente a cualquier día o turno.',
    'cal.week_card_hint': 'Arrastra a otro día o turno',
    // Route planner
    'route.map_aria': 'Mapa de la ruta',
    'route.err_map': 'No se pudo cargar el mapa.',
    'route.err_add_stop_save': 'Agrega al menos una parada antes de guardar.',
    'route.err_none_due_route': 'No hay clientes por atender en esta fecha que no estén ya en la ruta.',
    'route.err_load_due': 'No se pudieron cargar los clientes por atender.',
    'route.err_assign_all': 'Asigna cada cliente a un turno de Mañana, Tarde o Noche antes de aprobar.',
    'route.err_add_stop_approve': 'Agrega al menos una parada antes de aprobar.',
    'route.err_approve': 'No se pudo aprobar la ruta. Intenta de nuevo.',
    'route.err_save_first_end': 'Guarda la ruta antes de terminarla.',
    'route.confirm_end_shift': '¿Terminar la ruta de {shift}? Aparecerá en tus reportes.',
    'route.err_end': 'No se pudo terminar la ruta.',
    'route.err_save_first_complete': 'Guarda la ruta antes de marcar paradas como hechas.',
    'route.err_update_stop': 'No se pudo actualizar la parada.',
    'route.err_save_first': 'Guarda la ruta primero.',
    'route.confirm_clear_all': '¿Quitar todas las paradas de todas las rutas de hoy?',
    'route.err_slot_full': 'La ruta de {shift} está llena (máx. {max} paradas).',
    'route.err_save_route': 'No se pudo guardar la ruta.',
    'route.err_approve_first_nav': 'Aprueba la ruta antes de comenzar la navegación.',
    'route.navigate': 'Navegar',
    'route.save': 'Guardar',
    'route.done': 'Hecho',
    'route.pay': 'Pagar',
    'route.unassign': 'Quitar',
    'route.load_due': 'Cargar clientes por atender',
    'route.loading': 'Cargando…',
    'route.approve_route': 'Aprobar ruta',
    'route.approving': 'Aprobando…',
    'route.assign_n_to_shift': 'Asigna {n} a un turno',
    'route.clear_all': 'Quitar todas las paradas',
    'route.end_shift': 'Terminar ruta de {shift}',
    'route.ending': 'Terminando...',
    'route.saved_menu': 'Guardadas',
    'route.no_versions': 'Aún no hay rutas guardadas',
    'route.unassigned_crew': 'Sin cuadrilla asignada',
    'route.crew': 'Cuadrilla',
    'route.approved_ready': 'Aprobada · lista para navegar',
    'route.waiting_approval': 'Esperando aprobación',
    'route.total_due': 'Total a cobrar',
    'route.collected': 'Cobrado',
    'route.outstanding': 'Pendiente',
    'route.amt_due': '{amt} por cobrar',
    'route.amt_collected': '{amt} cobrado',
    'route.amt_balance': '{amt} de saldo',
    'route.amt_outstanding': '{amt} pendiente',
    'route.n_done': '{n} hechas',
    'route.unassigned': 'Sin asignar',
    'route.unassigned_title': 'Sin asignar ({n})',
    'route.unassigned_hint': 'Arrastra a una ruta o toca los botones del turno',
    'route.drop_here_shift': 'Suelta aquí para agregar a {shift}',
    'route.empty_shift_hint': 'Arrastra clientes aquí o toca el botón de turno en cada tarjeta',
    'route.marker_stop': 'Parada {letter}',
    'route.nav_shift_aria': 'Navegar la ruta de {shift}',
    'route.save_shift_aria': 'Guardar la ruta de {shift}',
    'route.nav_title_enabled': 'Abrir navegación paso a paso en Google Maps',
    'route.nav_title_disabled': 'Aprueba la ruta para habilitar la navegación',
    'route.version_saved': 'Guardada',
    'route.client_default': 'Cliente',
  },
};

const LanguageContext = createContext({
  lang: 'en',
  setLang: () => {},
  t: (k) => k,
});

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const saved = window.localStorage.getItem('ms-lang');
      if (saved === 'en' || saved === 'es') return saved;
    } catch (e) { /* ignore */ }
    const nav =
      (typeof navigator !== 'undefined' && (navigator.language || navigator.userLanguage)) ||
      'en';
    return String(nav).toLowerCase().startsWith('es') ? 'es' : 'en';
  });

  useEffect(() => {
    try { window.localStorage.setItem('ms-lang', lang); } catch (e) { /* ignore */ }
    if (typeof document !== 'undefined') document.documentElement.lang = lang;
  }, [lang]);

  const t = (key, vars) => {
    const dict = translations[lang] || translations.en;
    let str = dict[key];
    if (str == null) str = translations.en[key];
    if (str == null) return key;
    if (vars) {
      Object.keys(vars).forEach((k) => {
        str = str.split(`{${k}}`).join(String(vars[k]));
      });
    }
    return str;
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang: setLangState, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useTranslation() {
  return useContext(LanguageContext);
}
